import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getColors } from "../theme/colors";

const THEME_STORAGE_KEY = "pendpay_theme_mode";

const ThemeContext = createContext(null);

function applyDocumentTheme(colors, isDark) {
  const root = document.documentElement;
  root.style.setProperty("--pp-bg", colors.bg);
  root.style.setProperty("--pp-surface", colors.surface);
  root.style.setProperty("--pp-border", colors.border);
  root.style.setProperty("--pp-lime", colors.lime);
  root.style.colorScheme = isDark ? "dark" : "light";

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", colors.bg);

  if (document.body) {
    document.body.style.background = colors.bg;
    document.body.style.color = colors.text;
  }
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === "light" || stored === "dark" ? stored : "dark";
    } catch {
      return "dark";
    }
  });

  const colors = useMemo(() => getColors(mode), [mode]);
  const isDark = mode === "dark";

  useEffect(() => {
    applyDocumentTheme(colors, isDark);
  }, [colors, isDark]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ mode, isDark, colors, setMode, toggleTheme }),
    [mode, isDark, colors, setMode, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export function useColors() {
  return useTheme().colors;
}
